import { observer } from 'mobx-react-lite'
import React, { useMemo, useState } from 'react'
import classNames from 'classnames'

// evertel
import { InfoBox, Button, Col, Row,
    Modal, ModalBody, ModalHeader, ModalFooter,
    SuccessCheckmark, PasswordInput
} from '@evertel/web/ui'
import { AuthenticationController } from '@evertel/shared/feature-authentication-auth0'
import { useService } from '@evertel/di'
import { SessionService } from '@evertel/session/SessionService'
import { LogoutService } from '@evertel/session/LogoutService'
import { ResetPasswordController } from '@evertel/blue-user'

interface ChangePasswordModalProps {
    isOpen: boolean
    onClose: () => void
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = observer(({
    isOpen,
    onClose
}) => {

    const authenticationController = useService(AuthenticationController, [])
    const resetPasswordController = useService(ResetPasswordController, [])
    const logoutService = useService(LogoutService, [])
    const sessionService = useService(SessionService, [])

    const [error, setError] = useState('')
    const [currentPassword, setCurrentPassword] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [isNewPasswordValid, setIsNewPasswordValid] = useState(false)

    const currentUserId = sessionService.currentUserId
    const [passwordHasBeenReset, setPasswordHasBeenReset] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    useState(() => {
        resetPasswordController.init(currentUserId)
    })

    const submitPasswordReset = async () => {
        const currentPasswordTrim = currentPassword?.trim()
        const newPasswordTrim = newPassword?.trim()

        // make sure all fields are filled out
        if (!currentPasswordTrim || !newPasswordTrim) {
            setError('Please fill out all fields')
            return
        }

        if (!isNewPasswordValid) {
            setError('Your password does not meet our minimum strength requirements. Please revise and try again')
            return
        }

        const body = {
            oldPassword: currentPasswordTrim,
            newPassword: newPasswordTrim,
            confirmation: newPasswordTrim
        }

        try {
            setIsLoading(true)

            await authenticationController.changePassword(sessionService.currentUserId, body)

            setPasswordHasBeenReset(true)

            // auto close modal after checkmark animation is done
            setTimeout(() => {
                //user needs to be retrieved again, refresh should do it
                window.location.reload()
            }, 2000)

        } catch (error: any) {
            setError(error?.message || 'There was an error changing the password.')
        } finally {
            setIsLoading(false)
        }
    }

    // Memoize controller values and derived password expiration states in one hook
    const { daysBeforeExpires, departmentMaxPasswordAge, minimumDepartment, isExpired, neverExpires } = useMemo(() => {
        const daysBeforeExpires = resetPasswordController.passwordTTLdays
        const departmentMaxPasswordAge = resetPasswordController.minimumDepartmentPasswordTTLdays
        const minimumDepartment = resetPasswordController.minimumTTLDepartment

        // Derived states based on TTL days
        const isExpired = (daysBeforeExpires !== undefined) && daysBeforeExpires < 1
        const neverExpires = !daysBeforeExpires

        return {
            daysBeforeExpires,
            departmentMaxPasswordAge,
            minimumDepartment,
            isExpired,
            neverExpires
        }
    }, [
        resetPasswordController.passwordTTLdays,
        resetPasswordController.minimumDepartmentPasswordTTLdays,
        resetPasswordController.minimumTTLDepartment
    ])

    const onCheckClose = () => {
        if (!isExpired) {
            //only allow closing if it's not expired
            onClose()
        }
    }

    return (
        <Modal
            className="join-dept-modal"
            visible={isOpen}
            onClose={onCheckClose}
            backdrop={isExpired ? 'static' : true}
        >
            <ModalHeader
                closeButton={!isExpired}
                className={classNames('weight-900 h3 me-3', { 'text-danger justify-content-center': isExpired })}
                title={(isExpired) ? 'Your password has expired!' : 'Change your account password'}
            />
            <ModalBody>
                <Row>
                    <Col>
                        {!neverExpires && (
                            <InfoBox color="warning">
                                Your agency {minimumDepartment ? `(${minimumDepartment.name})` : ''} requires you to change your password every {departmentMaxPasswordAge} days.
                                <br /><strong>{isExpired ? 'Please reset to a new password.' : `Your current password expires in ${daysBeforeExpires} days.`}</strong>
                            </InfoBox>
                        )}
                        {/* ERROR BLOCK */}
                        {error && (
                            <InfoBox color="danger">
                                {error}
                            </InfoBox>
                        )}

                        {/* CURRENT PASSWORD */}
                        <PasswordInput
                            label="Current Password"
                            name="currentPassword"
                            value={currentPassword}
                            // autoComplete="off"
                            onChange={setCurrentPassword}
                            disabled={isLoading}
                            placeholder="Type your current password"
                            leftIcon="key"
                        />

                        <hr />

                        {/* NEW PASSWORD */}
                        <PasswordInput
                            label="New Password"
                            value={newPassword}
                            onChange={setNewPassword}
                            onValidate={setIsNewPasswordValid}
                            disabled={isLoading}
                            placeholder="Create a new password"
                            enablePasswordValidation
                        />
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter style={{ justifyContent: 'space-around' }}>
                {isExpired && !passwordHasBeenReset && (
                    <Col style={{ flexGrow: 0 }}>
                        <Button
                            onClick={() => { logoutService.logout('manual') }}
                            className="btn btn-warning float-right"
                        >
                            Logout
                        </Button>
                    </Col>
                )}

                {!passwordHasBeenReset ? (
                    <>
                        <Col />
                        <Col style={{ flexGrow: 0 }}>
                            <Button
                                color="primary"
                                onClick={submitPasswordReset}
                                className="float-right"
                                loading={isLoading}
                                disabled={!isNewPasswordValid || isLoading || !newPassword}
                            >
                                Update
                            </Button>
                        </Col>
                    </>
                ) : (
                    <Col style={{ flexGrow: 0 }}>
                        <SuccessCheckmark message={'Your password has been updated'} />
                    </Col>
                )}
            </ModalFooter>
        </Modal>

    )
})

export { ChangePasswordModal }
